import type { DatasetConfig, FilterFieldConfig } from '../types';
import { subsurfaceFillOpacityAtom } from '../subsurface-maps/atoms';

/**
 * A list filter over one facet: a searchable checkbox list with Select All,
 * matching the v3 mock's Basin / Interval / Type / Source / Created-by lists.
 * @param {string} id Filter id, also the facet key's snake_case form.
 * @param {string} label Label shown in the filter modal.
 * @param {string} param Request param the selected values are sent on.
 * @param {string} facetKey Response facet the options come from.
 * @return {FilterFieldConfig} The filter field config.
 */
const listFilter = (
  id: string,
  label: string,
  param: string,
  facetKey: string
): FilterFieldConfig => ({
  id,
  label,
  category: label,
  type: 'multi-select-list',
  param,
  facetKey,
});

/** The value filter's params, read back by the raster layer to hide cells. */
export const SUBSURFACE_VALUE_MIN_PARAM = 'min_value';
export const SUBSURFACE_VALUE_MAX_PARAM = 'max_value';

/**
 * Subsurface Maps (INS-12592): the subsurface_maps catalog as a Map Query
 * dataset. Read-only in Epic 1: every map is listed, filtered and drawn; nothing
 * is created or edited here.
 */
export const subsurfaceMapsConfig: DatasetConfig = {
  id: 'subsurfaceMaps',
  usesGlobalText: true,
  label: 'Petrotech Maps',
  searchEndpoint: '/queries/subsurface_maps',
  directHref: '/queries/subsurface_maps',
  tileEndpoint: '/queries/subsurface_maps/mvt',
  // Grey footprints as in the v3 mock, at 30% (UAT).
  defaultLayerColor: 'rgba(141, 141, 141, 0.3)',
  // The locator fits the map to the footprint's [west, south, east, north].
  extentKey: 'extent',
  // The locator also draws the row's cells (one map at a time; cells-layer.tsx).
  rasterLocator: true,
  // Selecting a map drops the other footprints to outlines; its cells replace its fill.
  tileFillOpacityAtom: subsurfaceFillOpacityAtom,
  highlightRowKey: 'id',
  highlightFeatureKey: 'id',
  defaultPayload: {
    text: '',
    page: 1,
    pageSize: 100,
  },

  // Each map's real data outline. Largest first (sort key = negated bbox area),
  // so small maps stay visible over big ones; the outline shares the fill's
  // source, so each tile is fetched once.
  tileLayers: [
    {
      geoAttribute: 'footprint',
      layerType: 'fill',
      minZoom: 0,
      fillOpacity: 0.1,
      outline: { opacity: 0.8 },
      sortKey: ['*', -1, ['get', 'bbox_area']],
    },
  ],

  // The value filter opens first, as in the mock.
  defaultFilterCategory: 'Thickness',

  filters: [
    // Hides cells outside the range on the shown map; never narrows the rows, and
    // the backend ignores both params. Every map today is a thickness map, so the
    // label is fixed; units come from the histogram facet.
    {
      id: 'value_range',
      label: 'Thickness',
      category: 'Thickness',
      type: 'value-histogram',
      minParam: SUBSURFACE_VALUE_MIN_PARAM,
      maxParam: SUBSURFACE_VALUE_MAX_PARAM,
      facetKey: 'valueHistogram',
    },
    listFilter('basin', 'Basin', 'basins', 'basin'),
    listFilter(
      'geologic_interval',
      'Geologic Interval',
      'geologic_intervals',
      'geologicInterval'
    ),
    listFilter('map_type', 'Map Type', 'map_types', 'mapType'),
    listFilter('layer_type', 'Layer Type', 'layer_types', 'layerType'),
    listFilter('source', 'Source', 'sources', 'source'),
    {
      id: 'created_at',
      label: 'Created At',
      category: 'Created At',
      type: 'date-range',
      minParam: 'min_created_at',
      maxParam: 'max_created_at',
    },
    {
      id: 'updated_at',
      label: 'Updated At',
      category: 'Updated At',
      type: 'date-range',
      minParam: 'min_updated_at',
      maxParam: 'max_updated_at',
    },
  ],

  // Column ids double as the backend's sort_attribute whitelist
  // (Query::SubsurfaceMaps::SORT_COLUMNS).
  // Widths are the v3 mock's, measured; names stay on one line as there.
  columns: [
    {
      id: 'name',
      header: 'Petrotech Map Name',
      accessorKey: 'name',
      size: 414,
      sortable: true,
    },
    {
      id: 'map_type',
      header: 'Map Type',
      accessorKey: 'mapType',
      size: 114,
      sortable: true,
    },
    {
      id: 'layer_type',
      header: 'Layer Type',
      accessorKey: 'layerType',
      size: 122,
      sortable: true,
    },
    {
      id: 'source',
      header: 'Source',
      accessorKey: 'source',
      size: 250,
      sortable: true,
    },
    {
      id: 'basin',
      header: 'Basin',
      accessorKey: 'basin',
      size: 113,
      sortable: true,
    },
    {
      id: 'geologic_interval',
      header: 'Geologic Interval',
      accessorKey: 'geologicInterval',
      size: 233,
      sortable: true,
    },
    // Created By reads the map's owner until the catalog records who created a
    // map (Epic 2).
    {
      id: 'created_by',
      header: 'Created By',
      accessorKey: 'owner',
      size: 124,
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 121,
      sortable: true,
      type: 'date',
    },
  ],
};
